import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/pages/Tools";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PenTool, Fingerprint, ScanSearch, Loader2, Download, Sparkles, Package } from "lucide-react";
import { toast } from "sonner";

const LOGO_STYLES = [
  { id: "minimalist", label: "Minimalist" },
  { id: "bold", label: "Bold" },
  { id: "elegant", label: "Elegant" },
  { id: "playful", label: "Playful" },
];

const TATTOO_STYLES = [
  { id: "fine_line", label: "Fine line" },
  { id: "old_school", label: "Old school" },
  { id: "realism", label: "Realism" },
  { id: "neo_trad", label: "Neo-traditional" },
];

const TATTOO_COLORS = ["black", "black and grey", "full colour"];
const PLACEMENTS = ["Arm", "Back", "Chest", "Leg", "Hand", "Neck"];

const Chips = ({ items, value, onChange, testid }) => (
  <div className="mt-2 flex flex-wrap gap-2" data-testid={testid}>
    {items.map((it) => {
      const id = it.id || it;
      const label = it.label || it;
      return (
        <button
          key={id}
          data-testid={`${testid}-${id}`}
          onClick={() => onChange(id)}
          className={`min-h-[40px] rounded-full px-4 text-sm capitalize transition-colors ${
            value === id ? "bg-brand text-white" : "bg-surfacealt text-ink-soft hover:text-ink"
          }`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const Preview = ({ busy, image, empty, testid, filename }) => (
  <div className="flex min-h-[340px] flex-col items-center justify-center rounded-3xl border border-white/10 bg-panel p-7">
    {busy && <Loader2 className="h-8 w-8 animate-spin text-brand" />}
    {!busy && !image && <p className="max-w-xs text-center text-sm text-ink-soft">{empty}</p>}
    {!busy && image && (
      <>
        <img
          data-testid={testid}
          src={`data:image/png;base64,${image.image_base64}`}
          alt="generated"
          className="w-full rounded-2xl border border-white/10"
        />
        <a
          data-testid={`${testid}-download`}
          href={`data:image/png;base64,${image.image_base64}`}
          download={filename}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm hover:bg-white/20"
        >
          <Download className="h-4 w-4" /> Download
        </a>
      </>
    )}
  </div>
);

export function LogoMaker() {
  const [name, setName] = useState("");
  const [spec, setSpec] = useState("");
  const [style, setStyle] = useState("minimalist");
  const [color, setColor] = useState("#1A7DFF");
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState(null);
  const [packing, setPacking] = useState(false);

  const downloadPack = async () => {
    setPacking(true);
    try {
      const { data } = await api.get(`/creations/${image.creation_id}/pack`, { responseType: "blob" });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = "chatmilu-logo-pack.zip";
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not build the pack");
    } finally {
      setPacking(false);
    }
  };

  const generate = async () => {
    if (!spec.trim()) return toast.error("Describe what your business does");
    setBusy(true);
    try {
      const { data } = await api.post("/tools/logo", {
        specialization: spec, business_name: name, style, base_color: color,
      });
      setImage(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      icon={PenTool}
      title="AI Logo Maker"
      blurb="Give your brand a name, say what you do, pick a style and colour — get a logo in seconds."
      testid="logo-tool-page"
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-white/10 bg-panel p-7">
          <label className="text-xs uppercase tracking-widest text-ink-soft">Business name (optional)</label>
          <Input
            data-testid="logo-name-input"
            dir="auto"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ChatMiLu"
            className="mt-2 h-11 rounded-xl"
          />

          <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Specialization</label>
          <Textarea
            data-testid="logo-spec-input"
            dir="auto"
            value={spec}
            onChange={(e) => setSpec(e.target.value)}
            placeholder="A specialty coffee roastery and slow bar in Hanoi…"
            className="mt-2 min-h-[100px] rounded-2xl"
          />

          <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Style</label>
          <Chips items={LOGO_STYLES} value={style} onChange={setStyle} testid="logo-style" />

          <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Brand colour</label>
          <div className="mt-2 flex items-center gap-3">
            <input
              data-testid="logo-color-input"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-11 w-16 cursor-pointer rounded-xl border border-white/10 bg-page"
            />
            <span className="text-sm text-ink-soft">{color}</span>
          </div>

          <Button
            data-testid="logo-generate-button"
            onClick={generate}
            disabled={busy}
            className="mt-8 min-h-[48px] w-full rounded-full bg-brand hover:bg-brand-hover active:scale-95"
          >
            {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Sparkles className="me-2 h-4 w-4" />}
            Generate logo
          </Button>
        </div>

        <div className="flex min-h-[340px] flex-col items-center justify-center rounded-3xl border border-white/10 bg-panel p-7">
          {busy && <Loader2 className="h-8 w-8 animate-spin text-brand" />}
          {!busy && !image && (
            <p className="max-w-xs text-center text-sm text-ink-soft">
              Your logo appears here. Try “an organic juice bar for runners”.
            </p>
          )}
          {!busy && image && (
            <>
              <img
                data-testid="generated-logo"
                src={`data:image/png;base64,${image.image_base64}`}
                alt="generated logo"
                className="w-full rounded-2xl border border-white/10"
              />
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <a
                  data-testid="generated-logo-download"
                  href={`data:image/png;base64,${image.image_base64}`}
                  download="chatmilu-logo.png"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm hover:bg-white/20"
                >
                  <Download className="h-4 w-4" /> PNG
                </a>
                <button
                  data-testid="logo-pack-button"
                  onClick={downloadPack}
                  disabled={packing || !image.creation_id}
                  className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm text-white hover:bg-brand-hover disabled:opacity-50"
                >
                  {packing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                  Download pack (.zip)
                </button>
              </div>
              <p className="mt-3 text-center text-xs text-ink-soft">
                Pack includes 1024px, 512px, a 256px favicon, a transparent cut-out and a dark-background version.
              </p>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}

export function TattooDesigner() {
  const [description, setDescription] = useState("");
  const [style, setStyle] = useState("fine_line");
  const [colors, setColors] = useState("black");
  const [placement, setPlacement] = useState("Arm");
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState(null);

  const generate = async () => {
    if (!description.trim()) return toast.error("Describe your dream tattoo");
    setBusy(true);
    try {
      const { data } = await api.post("/tools/tattoo", { description, style, colors, placement });
      setImage(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      icon={Fingerprint}
      title="AI Tattoo"
      blurb="Describe your dream tattoo, choose a style, palette and placement, and preview the design."
      testid="tattoo-tool-page"
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-white/10 bg-panel p-7">
          <label className="text-xs uppercase tracking-widest text-ink-soft">Your dream tattoo</label>
          <Textarea
            data-testid="tattoo-description-input"
            dir="auto"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A crane taking off over lotus flowers, with a thin crescent moon…"
            className="mt-2 min-h-[110px] rounded-2xl"
          />

          <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Style</label>
          <Chips items={TATTOO_STYLES} value={style} onChange={setStyle} testid="tattoo-style" />

          <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Colours</label>
          <Chips items={TATTOO_COLORS} value={colors} onChange={setColors} testid="tattoo-color" />

          <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Placement</label>
          <Chips items={PLACEMENTS} value={placement} onChange={setPlacement} testid="tattoo-placement" />

          <Button
            data-testid="tattoo-generate-button"
            onClick={generate}
            disabled={busy}
            className="mt-8 min-h-[48px] w-full rounded-full bg-brand hover:bg-brand-hover active:scale-95"
          >
            {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Sparkles className="me-2 h-4 w-4" />}
            Generate design
          </Button>
        </div>

        <Preview
          busy={busy}
          image={image}
          testid="generated-tattoo"
          filename="chatmilu-tattoo.png"
          empty="Your tattoo design appears here. Try “a compass wrapped in ivy, fine line”."
        />
      </div>
    </Shell>
  );
}

export function AIDetector() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const enough = words >= 50;

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/tools/detect", { text });
      setResult(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verdict = result?.verdict || (result?.report || "").match(/VERDICT:\s*(.+)/i)?.[1]?.trim();

  const heat = (score) => {
    if (score >= 70) return "bg-rose-500/25 text-rose-100 decoration-rose-400";
    if (score >= 40) return "bg-amber-400/20 text-amber-100 decoration-amber-300";
    return "bg-transparent";
  };

  return (
    <Shell
      icon={ScanSearch}
      title="AI Detector"
      blurb="Paste any text and get a verdict on whether it reads as AI-written or human, with the signals behind it."
      testid="detector-tool-page"
    >
      <div className="rounded-3xl border border-white/10 bg-panel p-7">
        <div className="flex items-baseline justify-between">
          <label className="text-xs uppercase tracking-widest text-ink-soft">Text to analyse</label>
          <span
            data-testid="detector-word-count"
            className={`text-xs ${enough ? "text-forest" : "text-ink-soft"}`}
          >
            {words} / 50 words
          </span>
        </div>
        <Textarea
          data-testid="detector-text-input"
          dir="auto"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste an essay, article, message or assignment (at least 50 words)…"
          className="mt-2 min-h-[220px] rounded-2xl"
        />

        <Button
          data-testid="detector-run-button"
          onClick={run}
          disabled={busy || !enough}
          className="mt-7 min-h-[48px] rounded-full bg-brand px-8 hover:bg-brand-hover active:scale-95"
        >
          {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <ScanSearch className="me-2 h-4 w-4" />}
          Analyse text
        </Button>

        {result && (
          <div className="mt-7 space-y-5" data-testid="detector-result">
            <div className="flex flex-wrap items-center gap-4">
              {verdict && (
                <span
                  data-testid="detector-verdict"
                  className="rounded-full bg-brand/15 px-4 py-2 text-sm font-semibold text-brand"
                >
                  {verdict}
                </span>
              )}
              {result.score != null && (
                <div className="min-w-[200px] flex-1">
                  <div className="flex items-center justify-between text-xs text-ink-soft">
                    <span>AI likelihood</span>
                    <span data-testid="detector-score">{result.score}%</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surfacealt">
                    <div
                      className="h-full bg-brand transition-all duration-700"
                      style={{ width: `${Math.min(100, result.score)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            {result.sentences?.length > 0 && (
              <div data-testid="detector-highlights">
                <div className="flex flex-wrap items-center gap-4 text-xs text-ink-soft">
                  <span className="uppercase tracking-widest">Sentence highlights</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-4 rounded-sm bg-rose-500/40" /> likely AI
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-4 rounded-sm bg-amber-400/40" /> uncertain
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-4 rounded-sm border border-white/20" /> reads human
                  </span>
                </div>
                <p dir="auto" className="mt-3 rounded-2xl bg-page p-5 text-sm leading-loose">
                  {result.sentences.map((s, i) => (
                    <span
                      key={`${i}-${s.text.slice(0, 24)}`}
                      title={`${s.score}% AI likelihood`}
                      data-testid={`detector-sentence-${i}`}
                      className={`rounded px-1 py-0.5 ${heat(s.score)}`}
                    >
                      {s.text}{" "}
                    </span>
                  ))}
                </p>
              </div>
            )}

            {result.signals?.length > 0 && (
              <ul data-testid="detector-signals" className="space-y-2 rounded-2xl bg-page p-5 text-sm leading-relaxed">
                {result.signals.map((s, i) => (
                  <li key={`${i}-${s.slice(0, 24)}`} dir="auto" className="flex gap-2">
                    <span className="text-brand">•</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            )}

            {result.note && (
              <p data-testid="detector-note" dir="auto" className="text-xs text-ink-soft">
                {result.note}
              </p>
            )}

            {!result.signals?.length && !result.sentences?.length && (
              <div
                data-testid="detector-report"
                dir="auto"
                className="whitespace-pre-wrap rounded-2xl bg-page p-5 text-sm leading-relaxed"
              >
                {result.report}
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}
